import React from 'react';
import { useTranslation } from 'react-i18next';

import Grid from '@mui/material/Grid';
import Modal from '@mui/material/Modal';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import Button from '@mui/material/Button';
import { alpha } from '@mui/material/styles';
import TextField from '@mui/material/TextField';
import IconButton from '@mui/material/IconButton';
import Typography from '@mui/material/Typography';
import InputAdornment from '@mui/material/InputAdornment';

import Iconify from '../../theme/iconify/iconify';
import { mislecAPI } from '../../util/axios';

function ChangePassword({ user, updateUser, open, onClose, setMessage, setSeverity, setOpen, setChangePasswordModal }) {
    const [currentPassword, setCurrentPassword] = React.useState('');
    const [newPassword, setNewPassword] = React.useState('');
    const [confirmPassword, setConfirmPassword] = React.useState('');
    const [showCurrentPassword, setShowCurrentPassword] = React.useState(false);
    const [showNewPassword, setShowNewPassword] = React.useState(false);
    const [showConfirmPassword, setShowConfirmPassword] = React.useState(false);

    const { t } = useTranslation();

    const changePassword = (oldPassword, newPassword, cofirmPassword) => {
        if (!oldPassword || !newPassword || !cofirmPassword) {
            //  Notify user of missing fields
            setMessage(t('profile.modals.password.error.fields'));
            setSeverity('warning');
            setOpen(true);
            return;
        }

        if (newPassword !== cofirmPassword) {
            //  Notify user of password mismatch
            setMessage(t('profile.modals.password.error.mismatch'));
            setSeverity('warning');
            setOpen(true);
            return;
        }

        //  Request body
        const data = {
            old_password: oldPassword,
            new_password: newPassword,
            confirm_password: cofirmPassword
        }

        //  Send data to the server
        mislecAPI.put(`/api/users/password/${user._id}`, data)
            .then(res => {
                //  Notify user of successful password change
                setMessage(t(res.data.detail));
                setSeverity('success');
                setOpen(true);
                setChangePasswordModal(false);
                setCurrentPassword('');
                setNewPassword('');
                setConfirmPassword('');
                setShowCurrentPassword(false);
                setShowNewPassword(false);
                setShowConfirmPassword(false);
            })
            .catch(error => {
                if (error.response.data.detail === 'api.auth.verify.401') {
                    //  Expired session
                    updateUser(null);
                } else {
                    //  Notify user of error
                    setMessage(error.response ? t(error.response.data.detail) : t('api.general.500'));
                    setSeverity('error');
                    setOpen(true);
                }
            });
    }

    return (
        <Modal
            open={open}
            onClose={() => {
                onClose(false);
                setCurrentPassword('');
                setNewPassword('');
                setConfirmPassword('');
                setShowCurrentPassword(false);
                setShowNewPassword(false);
                setShowConfirmPassword(false);
            }}
            sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
            }}
        >
            <Paper
                sx={{
                    padding: '24px',
                    minWidth: { xs: '95%', md: '40%' },
                    maxWidth: { xs: '95%', md: '40%' },
                }}
            >
                <Typography variant='h6'>
                    {t('profile.modals.password.title')}
                </Typography>
                <Typography variant='body2' sx={{ mt: 2, mb: 4 }}>
                    {t('profile.modals.password.description')}
                </Typography>
                <Grid item xs={12}>
                    <Grid container>
                        <Grid item xs={12} sx={{ mb: 4 }}>
                            <TextField
                                id='mISLec_profile_old_password'
                                name='password'
                                label={t('profile.modals.password.inputs.current')}
                                value={currentPassword}
                                onChange={(e) => setCurrentPassword(e.target.value)}
                                type={showCurrentPassword ? 'text' : 'password'}
                                fullWidth
                                InputProps={{
                                    endAdornment: (
                                        <InputAdornment position='end' sx={{ mr: 1 }}>
                                            <IconButton onClick={() => setShowCurrentPassword(!showCurrentPassword)} edge='end' sx={{ borderRadius: '8px' }}>
                                                <Iconify icon={showCurrentPassword ? 'eva:eye-fill' : 'eva:eye-off-fill'} />
                                            </IconButton>
                                        </InputAdornment>
                                    ),
                                }}
                            />
                        </Grid>
                        <Grid item xs={12} sx={{ mb: 4 }}>
                            <TextField
                                id='mISLec_profile_new_password'
                                name='password'
                                label={t('profile.modals.password.inputs.new')}
                                value={newPassword}
                                onChange={(e) => setNewPassword(e.target.value)}
                                type={showNewPassword ? 'text' : 'password'}
                                fullWidth
                                InputProps={{
                                    endAdornment: (
                                        <InputAdornment position='end' sx={{ mr: 1 }}>
                                            <IconButton onClick={() => setShowNewPassword(!showNewPassword)} edge='end' sx={{ borderRadius: '8px' }}>
                                                <Iconify icon={showNewPassword ? 'eva:eye-fill' : 'eva:eye-off-fill'} />
                                            </IconButton>
                                        </InputAdornment>
                                    ),
                                }}
                            />
                        </Grid>
                        <Grid item xs={12}>
                            <TextField
                                id='mISLec_profile_confirm_password'
                                name='password'
                                label={t('profile.modals.password.inputs.confirm')}
                                value={confirmPassword}
                                onChange={(e) => setConfirmPassword(e.target.value)}
                                type={showConfirmPassword ? 'text' : 'password'}
                                fullWidth
                                InputProps={{
                                    endAdornment: (
                                        <InputAdornment position='end' sx={{ mr: 1 }}>
                                            <IconButton onClick={() => setShowConfirmPassword(!showConfirmPassword)} edge='end' sx={{ borderRadius: '8px' }}>
                                                <Iconify icon={showConfirmPassword ? 'eva:eye-fill' : 'eva:eye-off-fill'} />
                                            </IconButton>
                                        </InputAdornment>
                                    ),
                                }}
                            />
                        </Grid>
                    </Grid>
                </Grid>
                <Stack
                    direction='row'
                    alignItems='center'
                    mt={3}
                    sx={{
                        justifyContent: {
                            xs: 'space-between',
                            md: 'flex-end'
                        }
                    }}
                >
                    <Button
                        variant=''
                        onClick={() => {
                            onClose(false);
                            setCurrentPassword('');
                            setNewPassword('');
                            setConfirmPassword('');
                            setShowCurrentPassword(false);
                            setShowNewPassword(false);
                            setShowConfirmPassword(false);
                        }}
                        sx={{
                            borderRadius: '8px !important',
                            backgroundColor: (theme) => ({
                                xs: alpha(theme.palette.error.main, 0.08),
                                md: 'rgba(33, 43, 54, 0.08)'
                            }),
                            color: {
                                xs: 'error.main',
                                md: 'inherit'
                            },
                            '&:hover': {
                                color: 'error.main',
                                backgroundColor: (theme) => alpha(theme.palette.error.main, 0.08)
                            }
                        }}
                    >
                        {t('profile.modals.password.buttons.cancel')}
                    </Button>
                    <Button
                        variant=''
                        onClick={() => {
                            changePassword(currentPassword, newPassword, confirmPassword);
                        }}
                        sx={{
                            ml: {
                                xs: 0,
                                md: 1
                            },
                            borderRadius: '8px !important',
                            backgroundColor: (theme) => ({
                                xs: alpha(theme.palette.primary.main, 0.08),
                                md: 'rgba(33, 43, 54, 0.08)'
                            }),
                            color: {
                                xs: 'primary.main',
                                md: 'inherit'
                            },
                            '&:hover': {
                                color: 'primary.main',
                                backgroundColor: (theme) => alpha(theme.palette.primary.main, 0.08)
                            }
                        }}
                    >
                        {t('profile.modals.password.buttons.change')}
                    </Button>
                </Stack>
            </Paper>
        </Modal>
    );
}

export default ChangePassword;